import { supabase } from './supabaseClient';

// ✅ Fetch all drafts + published/scheduled uploads
export async function fetchSupabaseDrafts(userId: string) {
  try {
    // ---- Fetch drafts ----
    const { data: draftsData, error: draftsError } = await supabase
      .from('drafts')
      .select('*')
      .eq('user_id', userId)
      .order('saved_at', { ascending: false });

    if (draftsError) console.error("❌ Failed to fetch drafts:", draftsError.message);

    const drafts = (draftsData || []).map((draft) => ({
      ...draft,
      id: draft.id,
      status: draft.status || 'draft',
      title: draft.title,
      description: draft.description,
      savedAt: draft.saved_at || null,
      scheduledAt: draft.scheduled_at || draft.scheduled_time || null,
      publishedAt: null,
      platform: draft.platform || 'youtube',
      customThumbnail:
        draft.customthumbnail ||
        draft.customThumbnail ||
        draft.custom_thumbnail ||
        draft.thumbnail_url || // ✅ Added this
        null,
      mediaType: draft.media_type || null,
      mediaUrl: draft.media_url || null,
    }));

    // ---- Fetch uploads ----
    const { data: uploadsData, error: uploadsError } = await supabase
      .from('uploads')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (uploadsError) console.error("❌ Failed to fetch uploads:", uploadsError.message);

    const uploads = (uploadsData || []).map((upload) => ({
      ...upload,
      id: `upload-${upload.youtube_video_id}`,
      status: upload.status || (upload.scheduled_time ? 'scheduled' : 'published'),
      title: upload.title,
      description: upload.description,
      savedAt: upload.updated_at || upload.created_at,
      scheduledAt: upload.scheduled_time || null,
      publishedAt: upload.published_at || null,
      platform: 'youtube',
      customThumbnail:
        upload.customthumbnail ||
        upload.customThumbnail ||
        upload.custom_thumbnail ||
        upload.thumbnail_url || // ✅ Added this
        upload.thumbnail ||
        null,
    }));

    return [...drafts, ...uploads];
  } catch (err: any) {
    console.error("❌ Error fetching drafts/uploads:", err.message);
    return [];
  }
}

// ✅ Delete draft by ID
export async function deleteSupabaseDraft(draftId: string) {
  const { error } = await supabase.from('drafts').delete().eq('id', draftId);
  if (error) {
    console.error("❌ Failed to delete draft:", error.message);
    return false;
  }
  return true;
}

// ✅ Upsert (insert or update) draft
export async function upsertSupabaseDraft(draft: any, userId: string) {
  const payload = {
    id: draft.id,
    user_id: userId,
    title: draft.title,
    description: draft.description,
    platform: draft.platform || 'youtube',
    media_url: draft.mediaUrl || null,
    media_type: draft.mediaType || null,
    customthumbnail: draft.thumbnailUrl || draft.customThumbnail || null,
    visibility: draft.visibility || 'public',
    scheduled_at: draft.scheduledAt?.trim() ? draft.scheduledAt : null,
    saved_at: new Date().toISOString(),
    status: 'draft',
  };

  const { data, error } = await supabase.from('drafts').upsert(payload, { onConflict: 'id' });
  if (error) {
    console.error('❌ Failed to upsert draft:', error.message);
    return { data: null, error };
  }
  return { data, error };
}

// ✅ Update existing draft
export async function updateSupabaseDraft(draftId: string, updates: any) {
  const fixedUpdates = { ...updates };

  // Normalize thumbnail keys
  if (updates.thumbnail_url) {
    fixedUpdates.customthumbnail = updates.thumbnail_url;
    delete fixedUpdates.thumbnail_url;
  }
  if (updates.customThumbnail) {
    fixedUpdates.customthumbnail = updates.customThumbnail;
    delete fixedUpdates.customThumbnail;
  }

  const { error } = await supabase
    .from('drafts')
    .update({ ...fixedUpdates, saved_at: new Date().toISOString() })
    .eq('id', draftId);

  if (error) {
    console.error("❌ Failed to update draft:", error.message);
    return false;
  }
  return true;
}

// ✅ Update existing upload (YouTube video)
export async function updateSupabaseUpload(videoId: string, updates: any) {
  const fixedUpdates = { ...updates };

  if (updates.customThumbnail) {
    fixedUpdates.customthumbnail = updates.customThumbnail;
    delete fixedUpdates.customThumbnail;
  }

  const { error } = await supabase
    .from('uploads')
    .update({ ...fixedUpdates, updated_at: new Date().toISOString() })
    .eq('youtube_video_id', videoId);

  if (error) {
    console.error("❌ Failed to update upload:", error.message);
    return false;
  }
  return true;
}

// ✅ Publish draft → Upload to YouTube → Insert into Supabase uploads → Delete draft
export async function publishDraftAndDelete(draft: any, userId: string) {
  try {
    const apiBase = import.meta.env.DEV ? "http://localhost:5002" : "";
    const res = await fetch(`${apiBase}/youtube-upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        fileUrl: draft.mediaUrl,
        metadata: {
          title: draft.title,
          description: draft.description,
          tags: draft.tags || [],
          privacyStatus: "public",
          scheduledTime: draft.scheduledAt || null,
          thumbnailUrl: draft.customThumbnail || null,
        },
      }),
    });

    const result = await res.json();
    if (!res.ok) throw new Error(result.message || "Failed to publish draft");

    // ✅ Insert into Supabase uploads table
    const { error: insertError } = await supabase.from('uploads').insert({
      user_id: userId,
      youtube_video_id: result.videoId,
      youtube_url: result.youtubeUrl,
      title: draft.title,
      description: draft.description,
      tags: draft.tags || [],
      media_url: draft.mediaUrl,
      media_type: draft.mediaType,
      thumbnail_url: draft.customThumbnail,
      visibility: "public",
      status: draft.scheduledAt ? "scheduled" : "published",
      scheduled_time: draft.scheduledAt || null,
      published_at: draft.scheduledAt ? null : new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    if (insertError) {
      console.error("❌ Failed to insert upload in Supabase:", insertError.message);
    }

    // ✅ Delete draft
    await deleteSupabaseDraft(draft.id);
    console.log(`✅ Draft ${draft.id} published and deleted`);
    return true;
  } catch (err: any) {
    console.error("❌ Failed to publish draft:", err.message);
    return false;
  }
}
